import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  CreditCard, RefreshCw, Loader2, AlertTriangle, FileWarning,
  Power, PlayCircle, CheckCircle2, History as HistoryIcon, ArrowLeft,
} from 'lucide-react';
import { paymentHoldAPI } from '../../utils/api';

const money = (v) =>
  typeof v === 'number'
    ? `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : '—';

function Section({ title, icon, children, action }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-800">{icon}{title}</h2>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

export default function PaymentHoldAdmin() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [settings, setSettings] = useState({ auto_enforce_enabled: false, large_booking_threshold: 5000 });
  const [held, setHeld] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [missing, setMissing] = useState([]);
  const [historyFor, setHistoryFor] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const loadSeq = useRef(0);

  const load = useCallback(async () => {
    const myId = ++loadSeq.current;
    setLoading(true);
    try {
      const [s, h, p, m] = await Promise.all([
        paymentHoldAPI.adminGetSettings(),
        paymentHoldAPI.adminHeld(),
        paymentHoldAPI.adminPreview(),
        paymentHoldAPI.adminMissingInvoices(),
      ]);
      if (myId !== loadSeq.current) return;  // a newer load / mutation superseded this one
      setSettings(s.data);
      setHeld(h.data.held || []);
      setCandidates(p.data.candidates || []);
      setMissing(m.data.missing || []);
    } catch (e) {
      if (myId === loadSeq.current) toast.error('Failed to load payment-hold data');
    } finally {
      if (myId === loadSeq.current) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleAuto = async () => {
    const next = !settings.auto_enforce_enabled;
    if (next && !window.confirm(
      'Turn ON automatic payment-hold enforcement? Clients/agencies with an unpaid 60% deposit (standard bookings) will be auto-held and auto-released on payment. Review the dry-run list below first.'
    )) return;
    setBusy('auto');
    try {
      const r = await paymentHoldAPI.adminSetSettings({ auto_enforce_enabled: next });
      loadSeq.current++;  // discard any in-flight load() so it can't overwrite this toggle
      setSettings((s) => ({ ...s, auto_enforce_enabled: r.data.auto_enforce_enabled }));
      toast.success(`Auto-enforcement ${r.data.auto_enforce_enabled ? 'ON' : 'OFF'}`);
    } catch { toast.error('Failed to update'); } finally { setBusy(''); }
  };

  const saveThreshold = async (val) => {
    setBusy('threshold');
    try {
      const r = await paymentHoldAPI.adminSetSettings({ large_booking_threshold: Number(val) });
      loadSeq.current++;  // discard any in-flight load()
      setSettings((s) => ({ ...s, large_booking_threshold: r.data.large_booking_threshold }));
      toast.success('Threshold saved');
    } catch { toast.error('Failed to save threshold'); } finally { setBusy(''); }
  };

  const runSweep = async () => {
    setBusy('sweep');
    try {
      const r = await paymentHoldAPI.adminSweep();
      toast.success(r.data.ran ? `Sweep done: ${JSON.stringify(r.data.actions || {})}` : 'Auto-enforcement is OFF — enable it first');
      await load();
    } catch { toast.error('Sweep failed'); } finally { setBusy(''); }
  };

  const backfill = async () => {
    if (!window.confirm('Generate the missing invoices for all confirmed-but-uninvoiced bookings? Consolidated events are billed on one invoice. No duplicates are created.')) return;
    setBusy('backfill');
    try {
      const r = await paymentHoldAPI.adminBackfillInvoices();
      toast.success(`Generated ${r.data.single_invoices} single + ${r.data.consolidated_invoices} consolidated invoice(s)`);
      await load();
    } catch { toast.error('Backfill failed'); } finally { setBusy(''); }
  };

  const release = async (userId) => {
    if (!window.confirm('Release this payment hold and restore full access?')) return;
    setBusy(`rel-${userId}`);
    try {
      await paymentHoldAPI.adminRelease(userId, { notes: 'Released from compliance dashboard' });
      toast.success('Hold released');
      await load();
    } catch { toast.error('Release failed'); } finally { setBusy(''); }
  };

  const verifyAch = async (userId) => {
    setBusy(`ach-${userId}`);
    try {
      await paymentHoldAPI.adminVerifyAch(userId, { notes: 'ACH verified from compliance dashboard' });
      toast.success('ACH verified — access restored');
      await load();
    } catch { toast.error('Verify failed'); } finally { setBusy(''); }
  };

  const openHistory = async (acct) => {
    setHistoryFor(acct);
    try {
      const r = await paymentHoldAPI.adminHistory(acct.user_id);
      setHistoryRows(r.data.history || []);
    } catch { setHistoryRows([]); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/admin/compliance')} data-testid="back-btn"
              className="p-2 rounded-lg hover:bg-slate-100"><ArrowLeft className="w-4 h-4" /></button>
            <div>
              <h1 className="flex items-center gap-2 text-lg font-bold text-slate-900">
                <CreditCard className="w-5 h-5 text-orange-600" /> Payment Holds
              </h1>
              <p className="text-xs text-slate-500">Manage On-Hold-for-Payment accounts, enforcement and billing audit.</p>
            </div>
          </div>
          <button onClick={load} disabled={loading} data-testid="refresh-btn"
            className="inline-flex items-center gap-1.5 text-sm rounded-lg border border-slate-200 px-3 py-2 hover:bg-slate-50">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-5">
        {/* Settings */}
        <Section title="Enforcement settings" icon={<Power className="w-4 h-4 text-slate-500" />}>
          <div className="flex flex-wrap items-center gap-6">
            <div className="flex items-center gap-3">
              <span className="text-sm text-slate-700">Automatic enforcement</span>
              <button onClick={toggleAuto} disabled={busy === 'auto'} data-testid="auto-enforce-toggle"
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings.auto_enforce_enabled ? 'bg-emerald-500' : 'bg-slate-300'}`}>
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings.auto_enforce_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
              <span className={`text-xs font-semibold ${settings.auto_enforce_enabled ? 'text-emerald-600' : 'text-slate-400'}`}
                data-testid="auto-enforce-state">{settings.auto_enforce_enabled ? 'ON' : 'OFF'}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-700">Large-booking review threshold ($)</span>
              <input type="number" min="0" key={`thr-${settings.large_booking_threshold}`}
                defaultValue={settings.large_booking_threshold}
                data-testid="threshold-input"
                onBlur={(e) => { if (Number(e.target.value) !== settings.large_booking_threshold) saveThreshold(e.target.value); }}
                className="w-28 rounded-md border border-slate-300 px-2 py-1 text-sm" />
              {busy === 'threshold' && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
            </div>
            <button onClick={runSweep} disabled={busy === 'sweep'} data-testid="run-sweep-btn"
              className="inline-flex items-center gap-1.5 text-sm rounded-lg bg-slate-800 text-white px-3 py-2 hover:bg-slate-700 disabled:opacity-60">
              {busy === 'sweep' ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />} Run enforcement sweep
            </button>
          </div>
          <p className="mt-3 text-xs text-slate-500">
            ACH and bookings at/above the threshold are flagged for your review instead of being auto-held. Approved alternative payment terms are exempt.
          </p>
        </Section>

        {/* Dry-run candidates */}
        <Section title={`Would be held / flagged (dry run) — ${candidates.length}`}
          icon={<AlertTriangle className="w-4 h-4 text-amber-500" />}>
          {candidates.length === 0 ? (
            <p className="text-sm text-slate-500">No accounts currently meet the hold criteria.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="candidates-table">
                <thead><tr className="text-left text-xs text-slate-500 border-b">
                  <th className="py-2">Account</th><th>Role</th><th>Outstanding</th><th>Overdue</th><th>Action if enforced</th>
                </tr></thead>
                <tbody>
                  {candidates.map((c) => (
                    <tr key={c.user_id} className="border-b last:border-0">
                      <td className="py-2">{c.email}</td>
                      <td className="capitalize">{c.role}</td>
                      <td>{money(c.outstanding_amount)}</td>
                      <td>{c.overdue ? <span className="text-rose-600">Yes</span> : 'No'}</td>
                      <td>{c.requires_review ? <span className="text-amber-600">Flag for review (ACH/large)</span> : <span className="text-rose-600">Auto-hold</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>

        {/* Currently held */}
        <Section title={`On Hold for Payment — ${held.length}`} icon={<CreditCard className="w-4 h-4 text-orange-500" />}>
          {held.length === 0 ? (
            <p className="text-sm text-slate-500">No accounts are currently on hold.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="held-table">
                <thead><tr className="text-left text-xs text-slate-500 border-b">
                  <th className="py-2">Account</th><th>Status</th><th>Type</th><th>Outstanding</th><th>Deadline</th><th></th>
                </tr></thead>
                <tbody>
                  {held.map((h) => (
                    <tr key={h.user_id} className="border-b last:border-0" data-testid={`held-row-${h.user_id}`}>
                      <td className="py-2">
                        <div className="font-medium">{h.company_name || h.email}</div>
                        <div className="text-xs text-slate-400">{h.email}</div>
                      </td>
                      <td>{h.status === 'ach_pending_verification' ? <span className="text-sky-600">ACH pending</span> : 'On hold'}</td>
                      <td className="capitalize">{h.hold_type}</td>
                      <td>{money(h.amount)}</td>
                      <td>{h.deadline || '—'}</td>
                      <td className="text-right whitespace-nowrap">
                        <button onClick={() => openHistory(h)} data-testid={`history-btn-${h.user_id}`}
                          className="text-xs text-slate-500 hover:text-slate-800 mr-3"><HistoryIcon className="w-4 h-4 inline" /></button>
                        {h.status === 'ach_pending_verification' && (
                          <button onClick={() => verifyAch(h.user_id)} disabled={busy === `ach-${h.user_id}`}
                            data-testid={`verify-ach-btn-${h.user_id}`}
                            className="text-xs rounded-md bg-sky-600 text-white px-2 py-1 mr-2 hover:bg-sky-700">Verify ACH</button>
                        )}
                        <button onClick={() => release(h.user_id)} disabled={busy === `rel-${h.user_id}`}
                          data-testid={`release-btn-${h.user_id}`}
                          className="text-xs rounded-md bg-emerald-600 text-white px-2 py-1 hover:bg-emerald-700 inline-flex items-center gap-1">
                          {busy === `rel-${h.user_id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} Release
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>

        {/* Missing invoices audit */}
        <Section title={`Confirmed bookings missing an invoice — ${missing.length}`}
          icon={<FileWarning className="w-4 h-4 text-rose-500" />}
          action={missing.length > 0 && (
            <button onClick={backfill} disabled={busy === 'backfill'} data-testid="backfill-btn"
              className="inline-flex items-center gap-1.5 text-sm rounded-lg bg-rose-600 text-white px-3 py-1.5 hover:bg-rose-700 disabled:opacity-60">
              {busy === 'backfill' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileWarning className="w-4 h-4" />} Generate missing invoices
            </button>
          )}>
          {missing.length === 0 ? (
            <p className="text-sm text-slate-500">Every confirmed booking has an invoice. ✓</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="missing-table">
                <thead><tr className="text-left text-xs text-slate-500 border-b">
                  <th className="py-2">Client</th><th>Email</th><th>Shift date</th>
                </tr></thead>
                <tbody>
                  {missing.slice(0, 50).map((m) => (
                    <tr key={m.booking_id} className="border-b last:border-0">
                      <td className="py-2">{m.company_name || '—'}</td>
                      <td>{m.client_email}</td>
                      <td>{m.shift_date || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>
      </div>

      {/* History drawer */}
      {historyFor && (
        <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={() => setHistoryFor(null)}>
          <div className="w-full max-w-md bg-white h-full p-6 overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="history-drawer">
            <h3 className="font-semibold text-slate-900 mb-1">Hold history</h3>
            <p className="text-xs text-slate-400 mb-4">{historyFor.email}</p>
            {historyRows.length === 0 ? (
              <p className="text-sm text-slate-500">No history.</p>
            ) : historyRows.map((r, i) => (
              <div key={i} className="border-l-2 border-slate-200 pl-3 pb-4">
                <div className="text-sm font-medium capitalize">{r.action.replace(/_/g, ' ')}</div>
                <div className="text-xs text-slate-500">
                  {r.created_at ? new Date(r.created_at).toLocaleString() : ''} · {r.actor_type}{r.actor_email ? ` (${r.actor_email})` : ''}
                </div>
                {r.reason && <div className="text-xs text-slate-600 mt-1">{r.reason}</div>}
                {typeof r.amount === 'number' && <div className="text-xs text-slate-600">Amount: {money(r.amount)}</div>}
                {r.notes && <div className="text-xs text-slate-400 italic mt-1">{r.notes}</div>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
